import { useEffect, useState } from "react";
import { Clock, X, ArrowRight } from "lucide-react";

const STORAGE_KEY = "lamal-banner-dismissed";

function computeDaysLeft(now) {
  const year = now.getFullYear();
  // Période ouverte : 1er octobre → 30 novembre 23:59 (résiliation LAMal)
  const start = new Date(year, 9, 1, 0, 0, 0);
  const deadline = new Date(year, 10, 30, 23, 59, 59);
  if (now < start || now > deadline) return null;
  const msPerDay = 86_400_000;
  return Math.max(0, Math.ceil((deadline - now) / msPerDay));
}

export default function LamalDeadlineBanner() {
  const [dismissed, setDismissed] = useState(false);
  const [daysLeft, setDaysLeft] = useState(null);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    setDismissed(sessionStorage.getItem(STORAGE_KEY) === "1");
    const params = new URLSearchParams(window.location.search);
    setPreview(params.get("lamal") === "preview");
    setDaysLeft(computeDaysLeft(new Date()));
    const id = setInterval(() => setDaysLeft(computeDaysLeft(new Date())), 60_000);
    return () => clearInterval(id);
  }, []);

  const visible = !dismissed && (daysLeft !== null || preview);
  if (!visible) return null;

  const display = daysLeft ?? 45; // preview affiche un compteur factice
  const plural = display > 1 ? "s" : "";
  const urgent = display <= 14;

  const dismiss = () => {
    sessionStorage.setItem(STORAGE_KEY, "1");
    setDismissed(true);
  };

  return (
    <div
      data-testid="lamal-deadline-banner"
      data-days={display}
      className="mt-0 bg-spi-red text-white shadow-md"
      role="region"
      aria-label="Période ouverte LAMal"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-12 py-3 flex items-center gap-3 md:gap-5">
        <div className="hidden sm:flex items-center justify-center w-9 h-9 rounded-full bg-white/15 flex-shrink-0">
          <Clock className="w-4.5 h-4.5" strokeWidth={2} />
        </div>

        <div className="flex-1 text-sm md:text-[15px] leading-tight">
          <span className="font-bold uppercase tracking-wide text-[11px] md:text-xs mr-2 inline-flex items-center gap-1">
            {urgent && (
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
            )}
            Période ouverte LAMal
          </span>
          <span className="opacity-95">
            Il vous reste{" "}
            <span
              className="font-bold tabular-nums px-1.5 py-0.5 bg-white/20 rounded"
              data-testid="lamal-days"
            >
              {display} jour{plural}
            </span>{" "}
            pour résilier ou changer votre caisse maladie.
          </span>
        </div>

        <a
          href="#contact"
          data-testid="lamal-cta"
          onClick={() => {
            if (typeof window !== "undefined" && typeof window.gtag === "function") {
              window.gtag("event", "lamal_banner_click", { days_left: display });
            }
          }}
          className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 bg-white text-spi-red font-semibold text-sm rounded-md hover:bg-slate-100 transition-colors flex-shrink-0"
        >
          Être accompagné
          <ArrowRight className="w-3.5 h-3.5" />
        </a>

        <button
          onClick={dismiss}
          aria-label="Fermer le bandeau"
          data-testid="lamal-dismiss"
          className="p-1.5 hover:bg-white/15 rounded transition-colors flex-shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
